import React from 'react';
import moment from 'moment';
// import {Link} from 'react-router';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import {Anchor} from '@albert-io/atomic';
import MessageBanner from 'generic/MessageBanner/MessageBanner.react';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {renderMoment} from 'lib/timeUtils';
import sessionStore from 'client/Session/SessionStore';

import './auto-archive-classrooms-banner.scss';

class AutoArchiveClassroomsBanner extends React.Component {
  static propTypes = {
    nextClassroomBeingArchived: PropTypes.instanceOf(List)
  };

  /**
   * Every year we auto-archive classes on/around 7/1. During 7/1 - 7/15
   * we display a banner letting teachers know...
   *
   * @returns {boolean} Whether or not it's auto-archive "season"
   */
  isAutoArchiveSeason() {
    return systemTimeOffsetStore.getCurrentTime().isBetween(
      moment()
        .month('July')
        .date(1)
        .startOf('day'),
      moment()
        .month('July')
        .date(15)
        .endOf('day')
    );
  }

  showUpcomingArchiveWarningBanner() {
    const nextClassroomBeingArchived = this.props.nextClassroomBeingArchived.first();
    return (
      nextClassroomBeingArchived !== undefined &&
      /**
       * We only show the banner if there is a classroom with an auto-archive date
       * that is less than two weeks out AND the date has not passed.
       */
      systemTimeOffsetStore
        .getCurrentTime()
        .add(2, 'weeks')
        .isAfter(nextClassroomBeingArchived.getAutoArchiveDate()) &&
      systemTimeOffsetStore
        .getCurrentTime()
        .isBefore(nextClassroomBeingArchived.getAutoArchiveDate())
    );
  }

  shouldShowBanner() {
    return this.isAutoArchiveSeason() || this.showUpcomingArchiveWarningBanner();
  }

  getNextArchiveDate() {
    const nextClassroomBeingArchivedDate = this.props.nextClassroomBeingArchived
      .first()
      .getAutoArchiveDate();
    return renderMoment(nextClassroomBeingArchivedDate, 'dateFormalShortWithFullMonth');
  }

  getMessageBannerContent() {
    if (this.isAutoArchiveSeason()) {
      return (
        <p>
          On July 1st, we automatically archived classes created before May 1st. Archiving a
          classroom makes it unavailable to students, and prevents additional enrollments. You can
          still view data from this class, but you will not be able to create new assignments.
          Unarchive your classes at any time by following the directions in this{' '}
          <Anchor
            as='a'
            target='_blank'
            rel='noopener noreferrer'
            href='https://help.albert.io/en/articles/1885019-how-do-i-archive-or-unarchive-a-class'
          >
            help article
          </Anchor>
          .
        </p>
      );
    }

    return (
      <p>
        On {this.getNextArchiveDate()}, Albert will automatically archive some of your classes.
        Archiving a classroom makes it unavailable to students, and prevents additional enrollments.
        You can still view data from this class, but you will not be able to create new assignments.
        Unarchive your class at any time by following the directions in this{' '}
        <Anchor
          as='a'
          target='_blank'
          rel='noopener noreferrer'
          href='https://help.albert.io/en/articles/1885019-how-do-i-archive-or-unarchive-a-class'
        >
          help article
        </Anchor>
        .
        {
          /**
           * @todo – When we use `archive_date` to derive whether or not a classroom is archived we will be able to
           * allow users to edit the date.
           * @see https://github.com/albert-io/project-management/milestone/49
           */
          // To change the archive date for any of your classes, <Link to='/classes/auto-archive-list'>click here</Link>.
        }
      </p>
    );
  }

  getMessageBannerHeading() {
    if (this.isAutoArchiveSeason()) {
      return `We're getting ready for next school year`;
    }
    return `Classes will be automatically archived on ${this.getNextArchiveDate()}`;
  }

  render() {
    if (!this.shouldShowBanner()) {
      return null;
    }
    return (
      <div className='auto-archive-classrooms-banner'>
        <MessageBanner
          icon='archive'
          heading={this.getMessageBannerHeading()}
          content={this.getMessageBannerContent()}
        />
      </div>
    );
  }
}

export default awaitMandarkQueries(
  () => ({
    queries: {
      nextClassroomBeingArchived: resource('classrooms_v1')
        .mandarkEndpoint(['teachers_v1', sessionStore.getUserId(), 'classrooms_v1'])
        .sort('auto_archive_date')
        .filter({
          status: 'active',
          auto_archive_date: {
            greater_than_inclusive: 'now'
          }
        })
        .pageSize(1)
    },
    loadingElement: null
  }),
  AutoArchiveClassroomsBanner
);
